 
import React, { FC } from 'react';
import AUReceiptComponent from '@aarhus-university/au-lib-react-components/src/components/AUReceiptComponent';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import { passwordLabels } from '@aarhus-university/au-lib-react-components/src/lib/i18n';
import { useRegistrationContext } from '../../lib/context';
import { useTranslationsContext } from '../../lib/translation';
import IdentityVerificationComponent from './IdentityVerificationComponent';

type Props = {
  confirmDetails: ISignupResult;
}

const ReceiptComponent: FC<Props> = ({
  confirmDetails,
}) => {
  const lang = useLangContext();
  // passwordLabels har kun da/en, mens LangContext giver en vilkaarlig string.
  // Faldet til 'en' for alt andet end 'da' foelger samme valg som gotoLink nedenfor.
  const labels = passwordLabels[lang === 'da' ? 'da' : 'en'];
  const translations = useTranslationsContext();
  const context = useRegistrationContext();
  const {
    key,
    successAccessGrantHeadingDa,
    successAccessGrantHeadingEn,
    successAccessGrantTextDa,
    successAccessGrantTextEn,
    activationReturnUrlDa,
    activationReturnUrlEn,
    activationReturnTextDa,
    activationReturnTextEn,
    successUserCreatedHeadingDa,
    successUserCreatedHeadingEn,
    successUserCreatedTextDa,
    successUserCreatedTextEn,
  } = context as ISignupContext;
  return (
    <>
      {confirmDetails && confirmDetails.result === 'AccessGrantedToExistingUser' && (
        <div className="page__content__block">
          <AUReceiptComponent
            receiptText={lang === 'da' ? successAccessGrantHeadingDa : successAccessGrantHeadingEn}
            helpText={<p className="text--larger">{lang === 'da' ? successAccessGrantTextDa : successAccessGrantTextEn}</p>}
            showGoTo
            buttons={[{
              gotoLink: lang === 'da' ? activationReturnUrlDa : activationReturnUrlEn,
              gotoText: lang === 'da' ? activationReturnTextDa : activationReturnTextEn,
            }]}
          />
        </div>
      )}
      {confirmDetails && confirmDetails.result === 'AccessGrantedToNewUser' && (
        <>
          <div className="page__content__block">
            <AUReceiptComponent
              receiptText={lang === 'da' ? successUserCreatedHeadingDa : successUserCreatedHeadingEn}
              helpText={<p className="text--larger">{lang === 'da' ? successUserCreatedTextDa : successUserCreatedTextEn}</p>}
              showGoTo
              buttons={[{
                gotoLink: lang === 'da' ? activationReturnUrlDa : activationReturnUrlEn,
                gotoText: lang === 'da' ? activationReturnTextDa : activationReturnTextEn,
              }]}
            />
          </div>
          <div className="page__content__block">
            <div className="box theme--background-secondary">
              <div className="row row--equal-width-items">
                <div className="row__item">
                  <h2 className="text--default-size text--pull">{labels.mailReceiptHelp}</h2>
                  <p>{labels.mailReceiptHelp3}</p>
                  <a className="a--text-link" href={`/${lang}/create-account/?context=${key}`}>{labels.mailReceiptLink}</a>
                </div>
                <div className="row__item">
                  <h2 className="text--default-size text--pull">{labels.supportHeader}</h2>
                  <a className="a--text-link" href={labels.supportStudentLink}>{labels.supportStudentText}</a>
                  <br />
                  <a className="a--text-link" href={labels.supportStaffLink}>{labels.supportStaffText}</a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {confirmDetails && confirmDetails.result === 'MatchingExistingUserFound' && (
        <div className="page__content__block">
          <h1>{translations.looksLikeYouAlreadyExist}</h1>
          <h2>{translations.didYouForget}</h2>
          <div className="button-container button-container--fixed-width button-container--vertical links--plain">
            <p><a href={`https://adgang.au.dk/${lang}/user/forgot`}>{translations.username}</a></p>
            <p><a href={`https://adgang.au.dk/${lang}/password/forgot`}>{translations.password}</a></p>
          </div>
          <p><a href={translations.itSupportUrl}>{translations.itSupportLinkText}</a></p>
          <p>
            {`${translations.supportMailText} `}
            <a href="mailto:support@au.dk">support@au.dk</a>
          </p>
        </div>
      )}
      {confirmDetails && confirmDetails.result === 'EmailVerificationRequired' && (
        <div className="page__content__block">
          <div className="receipt">
            <div className="receipt__icon icon-mailbox" aria-hidden="true" />

            <h2 className="receipt__header">
              {lang === 'da'
                ? (context as ISignupContext).verificationPendingHeadingDa || translations.emailVerificationRequired
                : (context as ISignupContext).verificationPendingHeadingEn || translations.emailVerificationRequired}
            </h2>
            <p className="text--large">
              {lang === 'da'
                ? (context as ISignupContext).verificationPendingTextDa || translations.emailVerificationRequiredText
                : (context as ISignupContext).verificationPendingTextEn || translations.emailVerificationRequiredText}
            </p>
          </div>
        </div>
      )}
      {confirmDetails && confirmDetails.result === 'FuzzyMatchIdentityVerificationRequired' && (
        <IdentityVerificationComponent
          fuzzyMatchCaseId={(confirmDetails as IUserSignupByFormResult).fuzzyMatchCaseId as string}
          obfuscatedCandidates={(confirmDetails as IUserSignupByFormResult).obfuscatedCandidates as IObfuscatedCandidate[]}
          allowDoubleIdentities={(context as ISignupContext).allowDoubleIdentities}
        />
      )}
    </>
  );
};

ReceiptComponent.displayName = 'ReceiptComponent';
export default ReceiptComponent;
